import { SearchModes } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePriceModePreference } from '@/app/common/priceModeProvider.tsx';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { getPriceDisplaySegment } from '@/app/common/priceDisplaySegment.ts';
import { SEARCH_TYPE } from '@/app/search/types.ts';

type PriceMode = {
  isFamilySearch: boolean;
  isSingleTraveller: boolean;
  priceTogglerEnabled: boolean;
  familyTotalPrice: boolean;
  totalPriceMode: boolean;
  showTotalPrice: boolean;
  showDoublePrice: boolean;
};

const usePriceMode = (searchParamsOverride?: SearchParams): PriceMode => {
  const { localization, searchParams: searchParamsFromContext, searchInfoData } = usePageWrapperData();
  const { isEvoLayout, isEvoResultsLayout } = useCommonClient();
  const { totalPricePreference } = usePriceModePreference();
  const searchParams = searchParamsOverride ?? searchParamsFromContext ?? {};
  const segment = getPriceDisplaySegment(searchParams);
  const isFamilySearch = segment === 'family';
  const doublePriceFamilyEnabled = localization?.configValuesApp?.doublePriceFamilyEnabled;
  const searchMode = searchParams?.[URLParameters.SEARCH_MODE] || searchInfoData?.searchMode;
  const isDP = searchMode === SearchModes.DP;
  const isDestinationsPage = searchInfoData?.searchType === SEARCH_TYPE.DESTINATIONS;
  const priceTogglerFt = localization?.configValuesApp?.priceTogglerEnabled;
  const familyTotalPriceFt = localization?.configValuesApp?.totalPriceForFamiliesEnabled;
  const isSingleTraveller = segment === 'single';
  const isEvoDP = Boolean(isEvoLayout && isDP && !isDestinationsPage);
  const priceTogglerEnabled = Boolean(priceTogglerFt && isEvoDP && segment === 'multiPax');
  const familyTotalPrice = Boolean(familyTotalPriceFt && isEvoDP && isFamilySearch);
  // Either test puts the funnel on the server's total prices; only the toggler adds a switch.
  const totalPriceMode = priceTogglerEnabled || familyTotalPrice;
  const isTotalSelected = totalPricePreference === 'total';
  const legacyFamilyTotals = isFamilySearch && isEvoResultsLayout;
  const legacyTotals = !totalPriceMode && legacyFamilyTotals;
  const showDoublePrice = legacyTotals && !!doublePriceFamilyEnabled;

  return {
    isFamilySearch,
    isSingleTraveller,
    priceTogglerEnabled,
    familyTotalPrice,
    totalPriceMode,
    showTotalPrice: familyTotalPrice || (priceTogglerEnabled && isTotalSelected) || (legacyTotals && !showDoublePrice),
    showDoublePrice,
  };
};

export default usePriceMode;
